import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Markdown } from '@tiptap/markdown';
import { marked } from 'marked';
import * as S from './styles';

interface MarkdownEditorProps {
	content: string;
	editable?: boolean;
	onChange?: (markdown: string) => void;
	placeholder?: string;
}

// marked@17 has a tokenizer bug: a paragraph or heading that follows an
// ordered list comes back with `.text` populated but `.tokens` left as
// an empty array, so MarkdownManager renders an empty paragraph/heading.
// We wrap marked.lexer once and re-run inline tokenization for any block
// where text exists but tokens is empty.

type AnyToken = { type?: string; text?: string; tokens?: AnyToken[]; items?: AnyToken[] };

const isBrokenInlineToken = (t: AnyToken): boolean =>
	(t?.type === 'paragraph' || t?.type === 'heading') &&
	Array.isArray(t.tokens) &&
	t.tokens.length === 0 &&
	typeof t.text === 'string' &&
	t.text.length > 0;

const fixupBrokenInlineTokens = (
	tokens: AnyToken[],
	inlineLexer: { inlineTokens: (s: string) => AnyToken[] }
): void => {
	if (!Array.isArray(tokens)) return;
	for (const t of tokens) {
		if (isBrokenInlineToken(t)) {
			t.tokens = inlineLexer.inlineTokens(t.text as string);
		}
		if (Array.isArray(t?.items)) {
			for (const item of t.items) {
				if (Array.isArray(item?.tokens)) fixupBrokenInlineTokens(item.tokens, inlineLexer);
			}
		}
	}
};

const PATCH_FLAG = '__markdownEditor_orderedListInlineFixup';
const markedAny = marked as unknown as Record<string, unknown> & {
	lexer: (src: string, opt?: unknown) => AnyToken[];
	Lexer: new () => { inlineTokens: (s: string) => AnyToken[] };
};
if (!markedAny[PATCH_FLAG]) {
	const originalLexer = markedAny.lexer;
	let inlineLexer: { inlineTokens: (s: string) => AnyToken[] } | null = null;
	markedAny.lexer = function patched(src: string, opt?: unknown) {
		const tokens = originalLexer.call(this, src, opt);
		if (!inlineLexer) inlineLexer = new markedAny.Lexer();
		fixupBrokenInlineTokens(tokens, inlineLexer);
		return tokens;
	};
	markedAny[PATCH_FLAG] = true;
}

// Use a `key` prop on this component to reset it when navigating
// to different content (e.g. a different guideline). The `content`
// prop is only used for initialization, not as a live binding.
const MarkdownEditor = ({
	content,
	editable = true,
	onChange,
	placeholder = 'Start writing...',
}: MarkdownEditorProps) => {
	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				heading: { levels: [1, 2, 3, 4] },
			}),
			Placeholder.configure({ placeholder }),
			Table.configure({ resizable: false }),
			TableRow,
			TableCell,
			TableHeader,
			Markdown.configure({ marked }),
		],
		content,
		contentType: 'markdown',
		editable,
		onUpdate: ({ editor, transaction }) => {
			// @tiptap/markdown re-sets content after editor creation, firing onUpdate
			// with docChanged:false. Those aren't real edits and must not round-trip to disk.
			if (!transaction.docChanged) return;
			if (onChange) onChange(editor.getMarkdown());
		},
	});

	useEffect(() => {
		if (editor) {
			editor.setEditable(editable);
		}
	}, [editor, editable]);

	if (!editor) return null;

	return (
		<S.Base>
			<EditorContent editor={editor} />
		</S.Base>
	);
};

export default MarkdownEditor;
